import React from "react";
import moment from "moment";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { serverFormat } from "../../../utils/dates";
import InboundActions from "./InboundActions";
import CardExpandable from "../../card/CardExpandable";
import DetailField from "../../card/DetailField";
import { movewareJobId } from "src/components/integrations/moveware/movewareJobDisplay";

export default function InboundRowMobile({
  item,
  variables,
  onEdit,
  onRemove,
  displayCustomerNotified = true,
  showProjectName = false,
  showTotalWeight = false,
  showBillingCharge = false,
  showMovewareDetails = false,
}) {
  const formatNotifiedDate = notifiedAt => {
    return moment(notifiedAt, serverFormat).format("ll");
  };

  const shipmentId = `${item.shipmentId}${
    item.revisionNumber > 0 ? `.R${item.revisionNumber}` : ""
  }`;

  const receivedTimestamp = item.receivedAt || item.completeTime;
  const receivedOnDate = receivedTimestamp
    ? moment(receivedTimestamp, serverFormat).format("ll")
    : "-";
  const receivedOnTime = receivedTimestamp
    ? moment(receivedTimestamp, serverFormat).format("h:mm A")
    : null;

  const completedBy = item.completer?.email || item.user.email;

  const formatTotalWeight = totalWeight => {
    if (totalWeight == null || totalWeight === "") return "-";

    return `${Number(totalWeight).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} lbs`;
  };

  const billingChargeTotal =
    item.billingCharges?.grand_total ??
    item.billingCharges?.grandTotal ??
    item.inboundCharges?.total;

  const formatBillingCharge = total => {
    if (total == null || total === "") return "-";

    return Number(total).toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
    });
  };

  const hasWeightMismatch = () => {
    const entered = Number(item.totalWeight || 0);
    const calculated = Number(item.calculatedTotalWeight || 0);
    return entered > 0 && calculated > 0 && Math.abs(entered - calculated) > 1;
  };

  return (
    <Card>
      <CardContent sx={{ "&:last-child": { pb: 2 } }}>
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography color="text.secondary" fontWeight={500}>
              {shipmentId}
            </Typography>
            {showProjectName && (
              <Typography color="text.secondary" fontWeight={500}>
                {item.project?.name}
              </Typography>
            )}
            {displayCustomerNotified && item.notifiedAt && (
              <Chip
                label={formatNotifiedDate(item.notifiedAt)}
                size="small"
                sx={{ borderRadius: 1, backgroundColor: "rgba(0, 0, 0, 0.05)" }}
              />
            )}
          </Box>
          <Typography mt={1} color="primary" sx={{ wordBreak: "break-word" }}>
            {completedBy}
          </Typography>
        </Box>
        <CardExpandable
          actions={
            <InboundActions item={item} variables={variables} onEdit={onEdit} onRemove={onRemove} />
          }
        >
          <Box display="flex" flexDirection="column" gap={1}>
            {showProjectName && <DetailField label="Project Name" value={item.project?.name || "-"} />}
            <DetailField label="Received On">
              {receivedOnTime ? (
                <Box display="flex" flexDirection="column">
                  <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                    {receivedOnDate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    {receivedOnTime}
                  </Typography>
                </Box>
              ) : (
                receivedOnDate
              )}
            </DetailField>
            {showMovewareDetails && (
              <DetailField label="Moveware Details" value={`Job ID: ${movewareJobId(item) || "—"}`} />
            )}
            {showTotalWeight && (
              <DetailField
                label="Inbound Weight"
                value={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <span>{formatTotalWeight(item.totalWeight)}</span>
                    {hasWeightMismatch() && (
                      <WarningAmberIcon color="warning" fontSize="small" titleAccess="Weight mismatch" />
                    )}
                  </Box>
                }
              />
            )}
            {showBillingCharge && (
              <DetailField label="Billing Charge" value={formatBillingCharge(billingChargeTotal)} />
            )}
            {displayCustomerNotified && item.notifiedAt && (
              <DetailField
                label="Customer Notified"
                value={moment(item.notifiedAt, serverFormat).format("lll")}
              />
            )}
          </Box>
        </CardExpandable>
      </CardContent>
    </Card>
  );
}
